import { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { AUTH_PROMO_FALLBACK_IMAGE } from "../../data/content";
import { useSiteContent } from "../../hooks/useSiteContent";

/**
 * Zone promo entre sous-titre et liste (connexion / inscription).
 * Image pilotée par le CMS (Admin → Réglages) avec repli sur image locale.
 * @param {{ compact?: boolean }} props — image plus basse sur mobile quand la colonne est compacte
 */
export function AuthSidePromoSlot({ compact = false }) {
  const { authPromo } = useSiteContent();

  const resolved = useMemo(() => {
    const cms = authPromo || {};
    const enabled = cms.enabled !== false;
    const raw = String(cms.image || "").trim();
    const image = raw || AUTH_PROMO_FALLBACK_IMAGE;
    const rawHref = String(cms.href || "").trim();
    const alt = String(cms.alt || "").trim() || "Euro Plomberie Piscines";
    return { enabled, image, href: rawHref, alt };
  }, [authPromo]);

  const [src, setSrc] = useState(resolved.image);

  useEffect(() => {
    setSrc(resolved.image);
  }, [resolved.image]);

  if (!resolved.enabled) return null;

  const external =
    typeof resolved.href === "string" &&
    (resolved.href.startsWith("http://") || resolved.href.startsWith("https://"));

  /** Évite les liens relatifs cassés (ex. `evenements` → `/connexion/evenements` au lieu de `/evenements`). */
  const internalTo =
    resolved.href && !external ? (resolved.href.startsWith("/") ? resolved.href : `/${resolved.href}`) : "";

  const body = (
    <img
      src={src}
      alt={resolved.alt}
      className={
        compact
          ? "h-36 w-full rounded-xl object-cover ring-1 ring-white/20 sm:h-40 lg:aspect-[16/9] lg:h-auto"
          : "aspect-[16/9] w-full rounded-xl object-cover ring-1 ring-white/20"
      }
      loading="lazy"
      onError={() => setSrc(AUTH_PROMO_FALLBACK_IMAGE)}
    />
  );

  const wrapClass =
    "block w-full max-w-none overflow-hidden rounded-xl transition-opacity hover:opacity-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F2B531]/60";

  let linked = <div className="w-full">{body}</div>;
  if (resolved.href) {
    if (external) {
      linked = (
        <a
          href={resolved.href}
          className={wrapClass}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={resolved.alt || "Voir l’offre"}
        >
          {body}
        </a>
      );
    } else {
      linked = (
        <Link to={internalTo || "/"} className={wrapClass}>
          {body}
        </Link>
      );
    }
  }

  return (
    <div
      className={`relative z-[1] w-full shrink-0 ${compact ? "mt-4 lg:mt-8" : "mt-8"}`}
      data-auth-promo-slot
    >
      {linked}
    </div>
  );
}
